import { useState } from 'react'
import type { AuditChangeEntry } from '@lms/shared-types'
import { LEAVE_TYPE_STYLE } from '../../../lib/leave-type-palette'
import { auditCategoryChip } from './audit-categories'

/** Resolve the chip text: a leave-type id abbreviates via the shared palette
 *  (annual → "Ann" → "ANN", matching the calendar legend); any other
 *  `subjectKind` (e.g. "Department", "Team") is shown as-is; otherwise the
 *  broad category. Chip CSS uppercases the result. */
function chipText(category: string, label?: string | null): string {
  if (label) {
    const style = (LEAVE_TYPE_STYLE as Record<string, { short: string }>)[label]
    return style ? `${style.short} Leave` : label
  }
  return auditCategoryChip(category)
}

/** Fields hidden from the rendered diff — either redundant with the row's own
 *  Entity (the object is already named) or internal plumbing HR never needs.
 *  The full payload stays on the wire; this only declutters the display. */
const HIDDEN_CHANGE_FIELDS = new Set([
  'employee_id', // redundant: the Entity already names the employee
  'scope_employee_id',
  'scope_type',
  'scope_leave_types',
  'assignment_type',
  'effective_from',
  'created_by',
  'updated_by',
  'created_at',
  'updated_at',
  'id',
])

/** Drop hidden/redundant fields from a change list before rendering. */
export function visibleChanges(
  changes: readonly AuditChangeEntry[],
): AuditChangeEntry[] {
  return changes.filter((c) => !HIDDEN_CHANGE_FIELDS.has(c.field))
}

/** A muted pill naming the audit event family on a row. A `label` override
 *  wins over the category (e.g. "Department"/"Team" instead of "Org"). */
export function CategoryChip({
  category,
  label,
}: {
  category: string
  label?: string | null
}): JSX.Element {
  return (
    <span
      data-testid="audit-category-chip"
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        padding: '2px 7px',
        borderRadius: 999,
        background: 'var(--surface)',
        border: '1px solid var(--french-gray-2)',
        color: 'var(--text-2)',
        whiteSpace: 'nowrap',
      }}
    >
      {chipText(category, label)}
    </span>
  )
}

/** Render one JSON-ish value compactly for a diff cell. `∅` marks an
 *  absent/null value (nothing there before, or cleared). */
export const EMPTY_MARKER = '∅'
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) return EMPTY_MARKER
  if (typeof value === 'string') return value === '' ? '""' : value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** Prettier labels for common raw column names so a diff reads in English.
 *  Unknown fields fall back to their snake_case name title-cased. */
const FIELD_LABELS: Record<string, string> = {
  employee_id: 'Employee',
  scope_employee_id: 'Employee',
  primary_approver: 'Approver',
  approver_employee_id: 'Approver',
  escalation_approver: 'Escalation approver',
  assignment_type: 'Assignment type',
  effective_from: 'Effective from',
  scope_leave_types: 'Leave types',
  scope_type: 'Scope',
  team_id: 'Team',
  created_by: 'Created by',
  updated_by: 'Updated by',
}

function fieldLabel(field: string): string {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field]
  const words = field.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field
}

/** A single field's change rendered as ~~before~~ → **after**, matching the
 *  CRM-style "Discovery → Qualified" affordance. The `field:` prefix is
 *  optional (omit inside a per-object timeline where the object is obvious). */
export function ChangeLine({
  field,
  before,
  after,
  showField = true,
}: {
  field: string
  before: unknown
  after: unknown
  showField?: boolean
}): JSX.Element {
  const beforeStr = formatValue(before)
  const afterStr = formatValue(after)
  return (
    <div style={{ fontSize: 12, lineHeight: 1.6 }} data-testid="change-line">
      {showField ? (
        <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{fieldLabel(field)}: </span>
      ) : null}
      <span
        style={{
          color: 'var(--text-3)',
          textDecoration: beforeStr === EMPTY_MARKER ? 'none' : 'line-through',
        }}
      >
        {beforeStr}
      </span>
      <span style={{ color: 'var(--text-3)', margin: '0 6px' }}>→</span>
      <span style={{ fontWeight: 700, color: 'var(--text)' }}>{afterStr}</span>
    </div>
  )
}

/**
 * Expandable field-level change diff for an audit row. Renders the
 * server-computed `changeSummary` as a readable "Field: before → after"
 * list. Self-hides when there are no changes.
 */
export function ChangeDiffExpander({
  changes,
}: {
  changes: readonly AuditChangeEntry[]
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const shown = visibleChanges(changes)
  if (shown.length === 0) return <span />

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 0 }}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={open ? 'Hide changes' : 'Show changes'}
        data-testid="change-diff-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0 4px',
          color: 'var(--text-3)',
          fontSize: 11,
          fontFamily: 'inherit',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          minHeight: 24,
        }}
      >
        {open ? '▾' : '▸'} {shown.length} change{shown.length === 1 ? '' : 's'}
      </button>
      {open ? (
        <div
          data-testid="change-diff-expander"
          style={{
            marginTop: 6,
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--surface)',
            border: '1px solid var(--french-gray-2)',
            minWidth: 240,
          }}
        >
          {shown.map((c) => (
            <ChangeLine key={c.field} field={c.field} before={c.before} after={c.after} />
          ))}
        </div>
      ) : null}
    </span>
  )
}
